import { useEffect } from "react";
import { useParams, useSearchParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, Mail, ChevronRight, Download, Printer } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { legal, LEGAL_META } from "../i18n/legal";
import { formatLegalDate } from "../i18n/legalUtils";
import { Reveal } from "../components/site/Reveal";
import "../legal-print.css";

export default function LegalPage() {
  const { doc } = useParams();
  const [params] = useSearchParams();
  const { lang } = useLang();
  const L = legal[lang];
  const d = L.docs[doc];
  const meta = LEGAL_META[doc];

  useEffect(() => {
    if (d) document.title = `${d.title} — ASTEO LUXEMBOURG S.A.`;
    return () => {
      document.title = "ASTEO LUXEMBOURG S.A. — Regulated European CFD Broker & Family Office";
    };
  }, [d]);

  useEffect(() => {
    if (d && params.get("print") === "1") {
      const t = setTimeout(() => window.print(), 600);
      return () => clearTimeout(t);
    }
  }, [d, params]);

  if (!d || !meta) return <Navigate to="/legal" replace />;

  const related = (meta.related || []).map((s) => ({ slug: s, title: L.docs[s]?.title })).filter((r) => r.title);

  return (
    <div data-testid="legal-page" className="legal-viewer bg-white">
      {/* Running footer for print */}
      <div className="print-running-footer print-only">
        <span>ASTEO LUXEMBOURG S.A. — CSSF P00000438 — LEI 222100L7BX1XCBJHH958</span>
        <span>{d.title} · v{meta.version}</span>
      </div>

      {/* Hero */}
      <section className="legal-hero-print relative bg-slate-900 pt-[136px] pb-16 overflow-hidden">
        <div
          className="legal-hero-grid absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <Link
            data-testid="legal-back-link"
            to="/legal"
            className="no-print inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-slate-400 hover:text-emerald-400 transition-colors duration-300"
          >
            <ArrowLeft size={14} /> {L.ui.back}
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-white leading-[1.08]"
            data-testid="legal-title"
          >
            {d.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-3xl"
          >
            {d.lead}
          </motion.p>

          {/* Document meta bar */}
          <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] font-mono uppercase tracking-[0.16em] text-slate-500">
            <span data-testid="legal-meta-version"><span className="text-slate-600">{L.ui.version}</span> {meta.version}</span>
            <span data-testid="legal-meta-effective"><span className="text-slate-600">{L.ui.effective}</span> {formatLegalDate(meta.effective, lang)}</span>
            <span data-testid="legal-meta-updated"><span className="text-slate-600">{L.ui.updated}</span> {formatLegalDate(meta.updated, lang)}</span>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-[240px_1fr] gap-10 lg:gap-16">
        {/* TOC */}
        <aside className="no-print hidden lg:block">
          <div className="sticky top-[100px]">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{L.ui.contents}</span>
            <nav className="mt-4 flex flex-col gap-2 border-l border-slate-200">
              {d.sections.map((s, i) => (
                <a
                  key={i}
                  data-testid={`legal-toc-${i}`}
                  href={`#sec-${i}`}
                  className="text-sm text-slate-500 hover:text-emerald-700 pl-4 -ml-px border-l border-transparent hover:border-emerald-700 transition-colors duration-300 leading-snug py-0.5"
                >
                  {s.h}
                </a>
              ))}
            </nav>
            <a
              data-testid="legal-print-btn-side"
              href={`?print=1`}
              onClick={(e) => { e.preventDefault(); window.print(); }}
              className="mt-8 inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-xs rounded-full hover:bg-emerald-800 transition-colors duration-300"
            >
              <Download size={13} /> {L.ui.print}
            </a>
          </div>
        </aside>

        {/* Article */}
        <article className="legal-article min-w-0">
          {d.sections.map((s, i) => (
            <Reveal key={i} delay={0.015 * i}>
              <section id={`sec-${i}`} data-testid={`legal-section-${i}`} className="pb-12 last:pb-0 scroll-mt-[100px]">
                <h2 className="font-serif text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">{s.h}</h2>
                <div className="mt-4 h-px w-12 bg-emerald-700" />
                {s.p?.map((para, k) => (
                  <p key={k} className="mt-5 text-sm md:text-base text-slate-600 leading-[1.85]">{para}</p>
                ))}
                {s.list && (
                  <ul className="mt-6 space-y-3 border-l border-slate-200 pl-6">
                    {s.list.map((li, k) => (
                      <li key={k} className="text-sm md:text-base text-slate-600 leading-[1.8] relative">
                        <span className="absolute -left-[25px] top-[0.7em] w-2 h-px bg-emerald-700" />
                        {li}
                      </li>
                    ))}
                  </ul>
                )}
                {s.after?.map((para, k) => (
                  <p key={k} className="mt-5 text-sm md:text-base text-slate-600 leading-[1.85]">{para}</p>
                ))}
              </section>
            </Reveal>
          ))}

          {/* Regulatory documents table (MiFID II) */}
          {d.docs && (
            <Reveal>
              <section data-testid="legal-docs-table" className="mt-4 border border-slate-200">
                <div className="px-6 py-5 border-b border-slate-200 bg-[#FAF9F6] flex items-center gap-3">
                  <FileText size={18} strokeWidth={1.6} className="text-emerald-700" />
                  <h2 className="font-serif text-lg font-semibold text-slate-900">{d.docsTitle}</h2>
                </div>
                <div className="hidden sm:grid grid-cols-[1fr_100px_90px_140px] px-6 py-3 border-b border-slate-200">
                  {[d.colDoc, d.colVersion, d.colFormat, ""].map((h, i) => (
                    <span key={i} className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{h}</span>
                  ))}
                </div>
                {d.docs.map(([name, ver, fmt], i) => (
                  <div key={i} data-testid={`legal-doc-row-${i}`} className="grid sm:grid-cols-[1fr_100px_90px_140px] gap-1 sm:gap-0 px-6 py-4 border-b border-slate-100 last:border-0 hover:bg-[#FAF9F6] transition-colors duration-300">
                    <span className="text-sm text-slate-800 pr-4">{name}</span>
                    <span className="text-xs font-mono text-slate-500 sm:self-center">{ver}</span>
                    <span className="text-xs font-mono text-slate-500 sm:self-center">{fmt}</span>
                    <a data-testid={`legal-doc-request-${i}`} href={`mailto:compliance@asteo.eu?subject=${encodeURIComponent(name)}`} className="no-print inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-900 transition-colors duration-300 sm:self-center sm:justify-end">
                      {d.action} <ChevronRight size={13} />
                    </a>
                  </div>
                ))}
                <p className="px-6 py-5 text-xs text-slate-500 leading-relaxed border-t border-slate-200 bg-[#FAF9F6]">{d.docsNote}</p>
              </section>
            </Reveal>
          )}

          {/* Related documents */}
          {related.length > 0 && (
            <Reveal>
              <div className="no-print mt-14">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{L.ui.relatedTitle}</span>
                <div className="mt-5 flex flex-col sm:flex-row gap-px bg-slate-200 border border-slate-200">
                  {related.map((r) => (
                    <Link key={r.slug} data-testid={`legal-related-${r.slug}`} to={`/legal/${r.slug}`} className="flex-1 bg-white px-6 py-6 group hover:bg-[#FAF9F6] transition-colors duration-300">
                      <span className="text-sm text-slate-900 group-hover:text-emerald-800 transition-colors duration-300">{r.title}</span>
                      <ChevronRight size={14} className="inline ml-2 text-slate-400 group-hover:translate-x-1 transition-transform duration-300" />
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          {/* Contact + print */}
          <Reveal>
            <div data-testid="legal-contact-block" className="mt-14 border border-slate-200 bg-[#FAF9F6] p-8 lg:p-10">
              <Mail size={20} strokeWidth={1.6} className="text-emerald-700" />
              <h3 className="mt-5 font-serif text-lg font-semibold text-slate-900">{L.ui.contactTitle}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{L.ui.contactBody}</p>
              <button
                data-testid="legal-print-btn"
                onClick={() => window.print()}
                className="no-print mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm rounded-full hover:bg-emerald-800 transition-colors duration-300"
              >
                <Printer size={15} /> {L.ui.print}
              </button>
            </div>
          </Reveal>

          <p className="mt-10 text-xs text-slate-400 leading-relaxed">{L.ui.disclaimer}</p>
          <Link data-testid="legal-back-home" to="/" className="no-print mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-slate-500 hover:text-emerald-700 transition-colors duration-300">
            <ArrowLeft size={13} /> {L.ui.backHome}
          </Link>
        </article>
      </div>
    </div>
  );
}
